/**
 * @file useCompoundOperations.js
 * @description Compound equipment and device assembly/disassembly operations
 *
 * Handles:
 * - Compound device assembly from components
 * - Compound disassembly
 * - Template-based compound creation
 * - Component validation
 * - Usage location tracking
 */

import { ref, computed } from 'vue';
import { useQuasar } from 'quasar';

export function useCompoundOperations() {
  // ============================================================================
  // STATE
  // ============================================================================

  const $q = useQuasar();

  /**
   * Compound assembly dialog visibility
   * @type {Ref<boolean>}
   */
  const compoundAssemblyDialogVisible = ref(false);

  /**
   * Compound form data
   * @type {Ref<Object>}
   */
  const compoundFormData = ref({
    name: '',
    brick_class: '',
    description: '',
    template_id: null,
    component_ids: [],
  });

  /**
   * Selected components for assembly
   * @type {Ref<Array>}
   */
  const selectedComponentsForAssembly = ref([]);

  /**
   * Available components for compound
   * @type {Ref<Array>}
   */
  const availableComponents = ref([]);

  /**
   * Loading state for operations
   * @type {Ref<boolean>}
   */
  const assemblingCompound = ref(false);

  /**
   * Component view dialog visibility
   * @type {Ref<boolean>}
   */
  const componentViewDialogVisible = ref(false);

  /**
   * Current compound being viewed
   * @type {Ref<Object|null>}
   */
  const currentCompound = ref(null);

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Check if can assemble compound
   */
  const canAssembleCompound = computed(() => {
    return (
      compoundFormData.value.name &&
      compoundFormData.value.brick_class &&
      selectedComponentsForAssembly.value.length > 0
    );
  });

  /**
   * Component count for current compound
   */
  const componentCount = computed(() => {
    return selectedComponentsForAssembly.value.length;
  });

  /**
   * Total I/O points for selected components
   */
  const totalIoPoints = computed(() => {
    let total = 0;

    selectedComponentsForAssembly.value.forEach((component) => {
      if (component.points && Array.isArray(component.points)) {
        total += component.points.length;
      }
    });

    return total;
  });

  // ============================================================================
  // COMPOUND ASSEMBLY
  // ============================================================================

  /**
   * Open compound assembly dialog
   * @param {Array} components - Available component devices
   * @param {Object} options - Assembly options
   */
  function openCompoundAssemblyDialog(components, options = {}) {

    availableComponents.value = components;
    selectedComponentsForAssembly.value = options.preselected || [];

    // Reset form
    compoundFormData.value = {
      name: options.name || '',
      brick_class: options.brick_class || '',
      description: options.description || '',
      template_id: options.template_id || null,
      component_ids: [],
    };

    compoundAssemblyDialogVisible.value = true;
  }

  /**
   * Close compound assembly dialog
   */
  function closeCompoundAssemblyDialog() {
    compoundAssemblyDialogVisible.value = false;
    compoundFormData.value = {
      name: '',
      brick_class: '',
      description: '',
      template_id: null,
      component_ids: [],
    };
    selectedComponentsForAssembly.value = [];
  }

  /**
   * Assemble compound from selected components
   * @param {Function} apiCallback - Async function to create compound via API
   * @returns {Promise<Object>} Created compound
   */
  async function assembleCompound(apiCallback) {
    if (!canAssembleCompound.value) {
      $q.notify({
        type: 'warning',
        message: 'Please fill in all required fields and select components',
      });
      return null;
    }

    try {
      assemblingCompound.value = true;

      // Prepare compound data
      const compoundData = {
        ...compoundFormData.value,
        component_ids: selectedComponentsForAssembly.value.map((c) => c.id),
        is_compound: true,
      };

      const result = await apiCallback(compoundData);

      $q.notify({
        type: 'positive',
        message: `Compound "${compoundData.name}" created with ${componentCount.value} components`,
      });

      closeCompoundAssemblyDialog();
      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create compound: ${error.message}`,
      });
      throw error;
    } finally {
      assemblingCompound.value = false;
    }
  }

  /**
   * Create compound from template
   * @param {Object} template - Compound template
   * @param {Function} apiCallback - Async function to instantiate via API
   * @returns {Promise<Object>} Created compound
   */
  async function createFromTemplate(template, apiCallback) {

    try {
      assemblingCompound.value = true;

      const result = await apiCallback({
        template_id: template.id,
        name: template.name, // Can be customized by user
        description: template.description,
      });

      $q.notify({
        type: 'positive',
        message: `Compound created from template "${template.name}"`,
      });

      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create from template: ${error.message}`,
      });
      throw error;
    } finally {
      assemblingCompound.value = false;
    }
  }

  // ============================================================================
  // COMPOUND DISASSEMBLY
  // ============================================================================

  /**
   * Disassemble compound into individual components
   * @param {Object} compound - Compound device/equipment
   * @param {Function} apiCallback - Async function to disassemble via API
   * @returns {Promise<Array>} Individual components
   */
  async function disassembleCompound(compound, apiCallback) {
    if (!compound.is_compound) {
      $q.notify({
        type: 'warning',
        message: 'Entity is not a compound',
      });
      return null;
    }

    try {
      const confirmed = await new Promise((resolve) => {
        $q.dialog({
          title: 'Disassemble Compound',
          message: `Disassemble "${compound.name}" into individual components? This action cannot be undone.`,
          cancel: true,
          persistent: false,
          focus: 'cancel',
        })
          .onOk(() => resolve(true))
          .onCancel(() => resolve(false));
      });

      if (!confirmed) {return null;}

      assemblingCompound.value = true;

      const result = await apiCallback({
        compound_id: compound.id,
      });

      $q.notify({
        type: 'positive',
        message: 'Compound disassembled successfully',
      });

      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to disassemble: ${error.message}`,
      });
      throw error;
    } finally {
      assemblingCompound.value = false;
    }
  }

  // ============================================================================
  // COMPONENT VALIDATION
  // ============================================================================

  /**
   * Validate components for compound assembly
   * @param {Array} components - Component devices
   * @returns {Object} Validation result
   */
  function validateComponents(components) {
    const errors = [];

    // Check minimum components
    if (components.length < 1) {
      errors.push('At least one component is required');
    }

    // Check for duplicate components
    const ids = components.map((c) => c.id);
    const uniqueIds = new Set(ids);
    if (ids.length !== uniqueIds.size) {
      errors.push('Duplicate components detected');
    }

    // Check if components are already part of another compound
    const alreadyInCompound = components.filter((c) => c.parent_compound_id);
    if (alreadyInCompound.length > 0) {
      errors.push(
        `${alreadyInCompound.length} component(s) already part of another compound`,
      );
    }

    // Check if components have compatible BRICK classes
    const hasIncompatibleClasses = validateBrickClassCompatibility(components);
    if (hasIncompatibleClasses) {
      errors.push('Components have incompatible BRICK classes');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate BRICK class compatibility for components
   * @param {Array} components - Component devices
   * @returns {boolean} True if incompatible
   */
  function validateBrickClassCompatibility(components) {
    // Basic validation: all components should be devices (not equipment)
    // More specific validation can be added based on BRICK Schema rules

    const nonDevices = components.filter((c) => c.type !== 'device');
    return nonDevices.length > 0;
  }

  // ============================================================================
  // COMPONENT VIEWING
  // ============================================================================

  /**
   * View components of compound
   * @param {Object} compound - Compound device/equipment
   */
  function viewComponents(compound) {

    currentCompound.value = compound;
    componentViewDialogVisible.value = true;
  }

  /**
   * Close component view dialog
   */
  function closeComponentView() {
    componentViewDialogVisible.value = false;
    currentCompound.value = null;
  }

  /**
   * Get component summary for compound
   * @param {Object} compound - Compound device/equipment
   * @returns {string} Human-readable summary
   */
  function getComponentSummary(compound) {
    if (!compound.is_compound || !compound.components) {
      return 'Not a compound';
    }

    const count = compound.components.length;
    const pointsCount = compound.components.reduce(
      (sum, c) => sum + (c.points?.length || 0),
      0,
    );

    return `${count} component${count !== 1 ? 's' : ''}, ${pointsCount} point${
      pointsCount !== 1 ? 's' : ''
    }`;
  }

  // ============================================================================
  // USAGE LOCATION TRACKING
  // ============================================================================

  /**
   * Track usage locations for compound template
   * @param {Object} template - Compound template
   * @param {Array} instances - Instances of this template
   * @returns {Array} Usage locations
   */
  function getCompoundUsageLocations(template, instances) {
    return instances.map((instance) => ({
      id: instance.id,
      name: instance.name,
      location: getInstanceLocation(instance),
      building: instance.building?.name || 'Unknown',
      space: instance.space?.name || 'Unknown',
      equipment: instance.equipment?.name || null,
    }));
  }

  /**
   * Get location string for instance
   * @param {Object} instance - Compound instance
   * @returns {string} Location string
   */
  function getInstanceLocation(instance) {
    const parts = [];

    if (instance.building?.name) {parts.push(instance.building.name);}
    if (instance.space?.name) {parts.push(instance.space.name);}
    if (instance.equipment?.name) {parts.push(instance.equipment.name);}

    return parts.length > 0 ? parts.join(' > ') : 'No location';
  }

  /**
   * Check if component can be added to compound
   * @param {Object} component - Component device
   * @returns {Object} Can add and reason
   */
  function canAddComponentToCompound(component) {
    if (component.parent_compound_id) {
      return {
        canAdd: false,
        reason: 'Component is already part of another compound',
      };
    }

    if (component.is_compound) {
      return {
        canAdd: false,
        reason: 'Cannot add a compound as a component',
      };
    }

    if (component.type !== 'device') {
      return {
        canAdd: false,
        reason: 'Only devices can be added as components',
      };
    }

    return {
      canAdd: true,
      reason: '',
    };
  }

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // State
    compoundAssemblyDialogVisible,
    compoundFormData,
    selectedComponentsForAssembly,
    availableComponents,
    assemblingCompound,
    componentViewDialogVisible,
    currentCompound,

    // Computed
    canAssembleCompound,
    componentCount,
    totalIoPoints,

    // Compound assembly
    openCompoundAssemblyDialog,
    closeCompoundAssemblyDialog,
    assembleCompound,
    createFromTemplate,

    // Compound disassembly
    disassembleCompound,

    // Component validation
    validateComponents,
    validateBrickClassCompatibility,

    // Component viewing
    viewComponents,
    closeComponentView,
    getComponentSummary,

    // Usage location tracking
    getCompoundUsageLocations,
    getInstanceLocation,
    canAddComponentToCompound,
  };
}
